<template>
  <AddDialogWithLoader
    :dialog-title="$route.meta.title"
    :right-panel-title="translate('pb2.addPresetView.selectedPresetsTitle')"
    :confirm-disabled="disableConfirm"
    :scroll="scrollAmount"
    :md-sizes="[40, 30, 30]"
    @add="add"
    @scroll="scrolled"
  >
    <template #header>
      <div>
        <PbInput
          v-model="searchString"
          dynamic
          :label="translate('pb2.addPresetView.searchLabel')"
        >
          <BaseButton
            :class="{
              'txt-muted': !searchString,
              'hover-error': !!searchString,
              'focus-error': true,
            }"
            :disabled="!searchString"
            @click="clearSearch"
          >
            <UiIcon
              icon="trash"
              size="small"
            />
          </BaseButton>
        </PbInput>
      </div>
    </template>
    <template #left>
      <TagFilters
        :route-name="(
          isGlobal
            ? routeNames.AddGlobalPresetTag
            : routeNames.AddLocalPresetTag
        )"
        :tag="profileStore.tags"
      />
    </template>

    <template #middleTitle>
      <TagBreadcrumb
        v-if="tag"
        :route-name="(
          isGlobal
            ? routeNames.AddGlobalPresetTag
            : routeNames.AddLocalPresetTag
        )"
        :tag="tag"
      />
      <template v-else>
        {{ translate('pb2.addPresetView.recommendedPresetsTitle') }}
      </template>
      <TagViewMode />
    </template>
    <template #middle>
      <PbDefaultItem
        v-for="preset in tag.defaultPresets"
        :key="preset.defaultUid"
        v-memo="[preset.defaultUid, tag.preferences.extended]"
        :item="preset"
        :action="translate('pb2.addPresetView.selectTooltip')"
        :extended="tag.preferences.extended"
        @click="select"
      />
      <template
        v-if="profileStore.presetSearchString"
      >
        <template
          v-if="profileStore.tags.references.All.defaultPresets.size"
        >
          <PbLink
            v-if="tag.name !== 'All'"
            class="pb-add-dialog-after-results"
            exact
            :to="viewMoreDestination"
          >
            {{ translate('pb2.addPresetView.viewMoreMessage') }}
          </PbLink>
        </template>
        <span
          v-else
          class="pb-add-dialog-after-results"
        >
          {{ translate('pb2.addPresetView.noResultsMessage') }}
        </span>
      </template>
    </template>

    <template #right>
      <template v-if="selectedPresets.length">
        <PbDefaultItem
          v-for="(preset, idx) in selectedPresets"
          :key="`${preset.defaultUid}_${idx}`"
          v-memo="[preset.defaultUid]"
          :item="preset"
          :action="translate('pb2.addPresetView.deselectTooltip')"
          @click="deselect(idx)"
        />
      </template>
      <template v-else>
        {{ translate('pb2.addPresetView.noSelectionMessage') }}
      </template>
    </template>
  </AddDialogWithLoader>
</template>

<script setup>
import {
  computed,
  toRef,
  ref,
  watch,
} from 'vue'
import UiIcon from '@nicolaudie-group/ng-ui-kit/components/UiIcon/UiIcon.vue'
import BaseButton from '@nicolaudie-group/ng-ui-kit/components/BaseButton/BaseButton.vue'
import useProfileStore from '@/stores/profile.store'
import usePresetStore from '@/stores/preset.store'
import AddDialogWithLoader from '@/components/AddDialog/AddDialogWithLoader.vue'
import PbInput from '@/components/PbInput.vue'
import useIsGlobal from '@/composables/useIsGlobal'
import TagFilters from '@/components/TagFilters.vue'
import TagBreadcrumb from '@/components/TagBreadcrumb.vue'
import PbDefaultItem from '@/components/PbDefaultItem/PbDefaultItem.vue'
import routeNames from '@/router/names'
import PbLink from '@/components/PbLink.vue'
import useDestination from '@/composables/useDestination'
import usePresetSearch from '@/composables/usePresetSearch'
import { useConstants, usePresets } from '@/composables/useProfile'
import useRecentlyUsedColorList from '@/composables/useRecentlyUsedColorList'
import useTranslate from '@/composables/useTranslate'
import TagViewMode from './TagViewMode.vue'

const { isGlobal } = useIsGlobal()

const { translate } = useTranslate()

const profileStore = useProfileStore()
const presetStore = usePresetStore()

const selectedPresets = ref([])

const tag = toRef(profileStore, 'tag')

const presets = usePresets()

const {
  searchInput: searchString,
  clear: clearSearch,
  scrolled,
  scrollAmount,
} = usePresetSearch()

watch(tag, () => {
  // When the tag changes, reset the scroll
  scrollAmount.value = 0
})

const viewMoreTo = computed(() => ({
  name: isGlobal.value ? routeNames.AddGlobalPresetTag : routeNames.AddLocalPresetTag,
  params: {
    tag: profileStore.tags.references.All.pathArr,
  },
}))

const viewMoreDestination = useDestination(viewMoreTo)

const toAdd = computed(() => {
  const amount = selectedPresets.value.length
  const size = Math.floor(256 / amount)
  const hasPresets = presets.value && presets.value.length

  return selectedPresets.value.map(({ defaultUid }, index) => (
    hasPresets ? {
      defaultUid,
    } : {
      defaultUid,
      // If there are no presets yet, specify dmx_start and dmx_end so that added presets are spread
      dmx_start: size * index,
      dmx_end: 255,
    }
  ))
})

const constants = useConstants()

const { prependMany } = useRecentlyUsedColorList()

const selectedColors = computed(() => {
  const { SSL_PR_COLOR } = constants.value.presets.types
  const items = selectedPresets.value
  const result = []
  for (let i = 0; i < items.length; i += 1) {
    const { preset_type: type, _color, colors } = items[i]
    if (type === SSL_PR_COLOR) {
      if (_color && _color.hexCode) {
        result.push(_color.hexCode)
      }
      if (colors) {
        result.push(...colors)
      }
    }
  }
  return result
})

const disableConfirm = computed(() => !selectedPresets.value.length)

function add() {
  if (selectedColors.value.length) {
    prependMany(selectedColors.value)
  }
  if (isGlobal.value) {
    return presetStore.createGlobal({
      // Keep only the defaultUid, other properties will be handled by the profile-sdk
      presets: toAdd.value,
    })
  }
  return presetStore.createLocal({
    // Keep only the defaultUid, other properties will be handled by the profile-sdk
    presets: toAdd.value,
  })
}

function select(preset) {
  selectedPresets.value.push(preset)
}

function deselect(idx) {
  selectedPresets.value.splice(idx, 1)
}
</script>
